import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Paper,
  Chip,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import api from "./../utils/api";
import { formatToLocalTime } from "../../src/utils/formatToLocalTime";

const DetailItem = ({ icon, label, value, valueColor }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: "14px",
      border: "1px solid rgba(47,99,173,0.08)",
      backgroundColor: "rgba(255,255,255,0.96)",
      boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
      height: "100%",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
      <Box sx={{ color: "#2f63ad", display: "flex", alignItems: "center" }}>
        {icon}
      </Box>
      <Typography variant="subtitle2" sx={{ color: "#6b778c", fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>

    <Typography
      variant="body1"
      sx={{
        color: valueColor || "#223142",
        fontWeight: 500,
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
  </Paper>
);

const UserProfile = () => {
  const [userData, setUserData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [editForm, setEditForm] = useState({ Name: "", Lastname: "", Email: "" });
  const [resetForm, setResetForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const username = localStorage.getItem("username");
    if (username) {
      api
        .get(`/users/${username}`)
        .then((res) => {
          setUserData(res.data);
          setEditForm({
            Name: res.data.Name || "",
            Lastname: res.data.Lastname || "",
            Email: res.data.Email || "",
          });
        })
        .catch((error) => console.error("Failed to fetch user profile:", error));
    }
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleEditSave = async () => {
    try {
      await api.put(`/users/${userData.UserID}`, {
        ...editForm,
        Roles: userData.Roles?.map((r) => r.RoleID) || [],
      });
      setUserData({ ...userData, ...editForm });
      setEditOpen(false);
      showSnackbar("Profile updated successfully.");
    } catch (err) {
      console.error("Failed to update user:", err);
      showSnackbar("Error updating profile.", "error");
    }
  };

  const closeResetDialog = () => {
    setResetOpen(false);
    setResetForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setResetStep(1);
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  };

  if (!userData) return null;

  const fullName = `${userData.Name || ""} ${userData.Lastname || ""}`.trim();
  const rolesText = userData.Roles?.map((r) => r.RoleName).join(", ") || "No Role";

  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: { xs: 2, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 1100,
          borderRadius: "20px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
          backgroundColor: "rgba(255,255,255,0.96)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: "32%" },
              background: "linear-gradient(180deg, rgba(47,99,173,0.06), rgba(47,99,173,0.02))",
              borderRight: { xs: "none", md: "1px solid rgba(47,99,173,0.08)" },
              borderBottom: { xs: "1px solid rgba(47,99,173,0.08)", md: "none" },
              p: { xs: 3, md: 4 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Avatar
              sx={{
                width: 104,
                height: 104,
                mb: 2,
                fontSize: "2rem",
                fontWeight: 700,
                backgroundColor: "#2f63ad",
                boxShadow: "0 8px 20px rgba(47,99,173,0.28)",
              }}
            >
              {userData.Name?.charAt(0) || "U"}
            </Avatar>

            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
            >
              {fullName || "User"}
            </Typography>

            <Typography variant="body2" sx={{ color: "#6b778c", mb: 1 }}>
              {userData.Username}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 1,
                mb: 2,
              }}
            >
              {(userData.Roles?.length
                ? userData.Roles
                : [{ RoleName: "No Role" }]
              ).map((role, index) => (
                <Chip
                  key={`${role.RoleName}-${index}`}
                  label={role.RoleName}
                  size="small"
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "rgba(47,99,173,0.08)",
                    color: "#2f63ad",
                    fontWeight: 600,
                  }}
                />
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1.25,
                flexWrap: "wrap",
                justifyContent: "center",
                mt: 1,
              }}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
                sx={primaryButtonSx}
                onClick={() => setEditOpen(true)}
              >
                Edit Profile
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<LockResetIcon />}
                sx={secondaryButtonSx}
                onClick={() => setResetOpen(true)}
              >
                Reset Password
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              width: { xs: "100%", md: "68%" },
              p: { xs: 3, md: 4 },
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
              >
                Profile Details
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Review and manage your account information.
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DetailItem
                  icon={<PersonOutlineIcon fontSize="small" />}
                  label="Full Name"
                  value={fullName || "—"}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailItem
                  icon={<EmailOutlinedIcon fontSize="small" />}
                  label="Email"
                  value={userData.Email || "—"}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailItem
                  icon={<VerifiedUserIcon fontSize="small" />}
                  label="Status"
                  value={userData.Status || "—"}
                  valueColor={
                    userData.Status?.toLowerCase() === "active"
                      ? "#2e7d32"
                      : "#d32f2f"
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailItem
                  icon={<VerifiedUserIcon fontSize="small" />}
                  label="Roles"
                  value={rolesText}
                />
              </Grid>

              <Grid item xs={12}>
                <DetailItem
                  icon={<AccessTimeIcon fontSize="small" />}
                  label="Last Login"
                  value={
                    userData.LastLogin
                      ? formatToLocalTime(userData.LastLogin)
                      : "Never"
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          Edit Profile
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            Update your profile information below.
          </Typography>

          <TextField
            fullWidth
            label="First Name"
            value={editForm.Name}
            onChange={(e) => setEditForm({ ...editForm, Name: e.target.value })}
            sx={fieldSx}
          />

          <TextField
            fullWidth
            label="Last Name"
            value={editForm.Lastname}
            onChange={(e) => setEditForm({ ...editForm, Lastname: e.target.value })}
            sx={fieldSx}
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={editForm.Email}
            onChange={(e) => setEditForm({ ...editForm, Email: e.target.value })}
            sx={fieldSx}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button onClick={() => setEditOpen(false)} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleEditSave} sx={primaryButtonSx}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resetOpen}
        onClose={closeResetDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          Reset Password
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            {resetStep === 1
              ? "Verify your current password to continue."
              : "Enter and confirm your new password."}
          </Typography>

          {resetStep === 1 ? (
            <TextField
              fullWidth
              label="Current Password"
              type="password"
              value={resetForm.currentPassword}
              onChange={(e) =>
                setResetForm({ ...resetForm, currentPassword: e.target.value })
              }
              sx={fieldSx}
            />
          ) : (
            <>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={resetForm.newPassword}
                onChange={(e) =>
                  setResetForm({ ...resetForm, newPassword: e.target.value })
                }
                sx={fieldSx}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={resetForm.confirmPassword}
                onChange={(e) =>
                  setResetForm({ ...resetForm, confirmPassword: e.target.value })
                }
                sx={fieldSx}
              />

              {resetForm.confirmPassword && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        resetForm.newPassword === resetForm.confirmPassword
                          ? "#2e7d32"
                          : "#d32f2f",
                      fontWeight: 600,
                    }}
                  >
                    {resetForm.newPassword === resetForm.confirmPassword
                      ? "Passwords match"
                      : "Passwords do not match"}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button onClick={closeResetDialog} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>

          {resetStep === 1 ? (
            <Button
              variant="contained"
              sx={primaryButtonSx}
              onClick={async () => {
                try {
                  await api.post("/verify-current-password", {
                    currentPassword: resetForm.currentPassword,
                  });
                  setResetStep(2);
                } catch (err) {
                  showSnackbar("Current password is incorrect.", "error");
                }
              }}
              disabled={!resetForm.currentPassword}
            >
              Verify
            </Button>
          ) : (
            <Button
              variant="contained"
              sx={primaryButtonSx}
              onClick={async () => {
                if (resetForm.newPassword !== resetForm.confirmPassword) {
                  showSnackbar("Passwords do not match.", "error");
                  return;
                }

                try {
                  await api.post("/reset-own-password", {
                    currentPassword: resetForm.currentPassword,
                    newPassword: resetForm.newPassword,
                  });
                  closeResetDialog();
                  showSnackbar("Password updated successfully.");
                } catch (err) {
                  showSnackbar(
                    err.response?.data?.message || "Password reset failed.",
                    "error"
                  );
                }
              }}
              disabled={
                !resetForm.newPassword ||
                !resetForm.confirmPassword ||
                resetForm.newPassword !== resetForm.confirmPassword
              }
            >
              Submit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserProfile;
